import type { AthleteProfileInput, AthleteProfileRecord } from '~/models/athleteProfile'
import type { ValidationIssues } from '~/models/common'
import type { GoalInput, GoalRecord } from '~/models/goal'
import type { PhaseInput, PhaseRecord } from '~/models/phase'
import type { DisplayPreferencesInput, SettingsRecord, WeekStartDay } from '~/models/settings'
import { validateDisplayPreferences } from '~/models/settings'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { getActiveGoal, updateGoal } from '~/storage/repositories/goalRepository'
import { getActivePhase, updateActivePhase } from '~/storage/repositories/phaseRepository'
import { getProfile, saveProfile } from '~/storage/repositories/profileRepository'
import { getSettings, updateSettings } from '~/storage/repositories/settingsRepository'
import type { Centimetres } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { requestWeekStartDayChange } from './weekBoundaryService'

/**
 * Editing what setup collected.
 *
 * Onboarding writes profile, phase and goal once and then has no way back in;
 * this is that way back. Each edit is written on its own rather than as one
 * transaction, because unlike onboarding these are independent user actions —
 * changing display units has nothing to do with the goal, and a failure in one
 * should not roll back another the user already confirmed.
 *
 * **Scope is deliberately bounded.** Only the *active* phase is editable, and
 * neither completed phases nor the transition records between them are — those
 * are **L-003** and inventing rules for them here would resolve an open decision
 * in code. Selective deletion belongs to Checkpoint 4 and is absent; a full
 * reset lives in `services/resetService.ts` (D-067).
 */

export interface EditableRecords {
  readonly profile: AthleteProfileRecord | undefined
  readonly phase: PhaseRecord | undefined
  readonly goal: GoalRecord | undefined
  readonly settings: SettingsRecord
}

/** Everything the Settings screen edits, read in one pass. */
export async function getEditableRecords(
  db: SolaDatabase,
  now: Date,
): Promise<Result<EditableRecords, StorageError>> {
  const profile = await getProfile(db)
  if (!profile.ok) return profile

  const phase = await getActivePhase(db)
  if (!phase.ok) return phase

  const goal = await getActiveGoal(db)
  if (!goal.ok) return goal

  const settings = await getSettings(db, now)
  if (!settings.ok) return settings

  return ok({
    profile: profile.value,
    phase: phase.value,
    goal: goal.value,
    settings: settings.value,
  })
}

/**
 * Editing height changes every future Navy estimate.
 *
 * Stored sessions are left exactly as they are: each carries the
 * `sourceHeightCm` its estimate was computed from, so past results stay
 * interpretable rather than being silently recomputed under a new height.
 */
export async function saveProfileEdits(
  db: SolaDatabase,
  input: AthleteProfileInput,
  now: Date,
): Promise<Result<AthleteProfileRecord, StorageError>> {
  return saveProfile(db, input, now)
}

export async function saveGoalEdits(
  db: SolaDatabase,
  goal: GoalRecord,
  input: GoalInput,
  now: Date,
): Promise<Result<GoalRecord, StorageError>> {
  return updateGoal(db, goal.id, input, now)
}

/**
 * The phase type is taken from the stored record, never from the form.
 *
 * All five phases are selectable from Checkpoint 2.4, so the original reason —
 * that a type control would be a dead one — no longer applies. The type stays
 * unchangeable here for a different and stronger reason: **changing the type of
 * a running phase would reinterpret every week already recorded against it**,
 * and the weeks the user has been answered about would silently mean something
 * else. Starting a different phase is a transition, and a transition is an
 * explicit decision (D-020). The registry remains the single authority on what
 * may be selected.
 */
export async function savePhaseEdits(
  db: SolaDatabase,
  phase: PhaseRecord,
  input: Omit<PhaseInput, 'type'>,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  return updateActivePhase(db, phase.id, { ...input, type: phase.type }, now)
}

/**
 * `StorageError` also carries a `kind`, so the validation variant uses a tag
 * that cannot collide with any `StorageErrorKind`, keeping this a genuine
 * discriminated union (D-056).
 */
export interface PreferencesValidationError {
  readonly kind: 'preferencesInvalid'
  readonly issues: ValidationIssues
}

export type PreferencesError = PreferencesValidationError | StorageError

export async function saveDisplayPreferences(
  db: SolaDatabase,
  input: DisplayPreferencesInput,
  now: Date,
): Promise<Result<SettingsRecord, PreferencesError>> {
  const issues = validateDisplayPreferences(input)
  if (issues.length > 0) return err({ kind: 'preferencesInvalid', issues })

  /* The week start does NOT go in the patch below, and this is the one line of
     this function that matters (D-105 §8). Writing it here would apply it
     immediately and reinterpret the week already running, which a materialised
     week is authoritative for. It goes through the request path, which either
     queues it for the next boundary or cancels a queued one. */
  const requested = await requestWeekStartDayChange(db, input.weekStartDay as WeekStartDay, now)
  if (!requested.ok) return err(requested.error)

  const written = await updateSettings(
    db,
    {
      weightDisplayUnit: input.weightDisplayUnit as SettingsRecord['weightDisplayUnit'],
      lengthDisplayUnit: input.lengthDisplayUnit as SettingsRecord['lengthDisplayUnit'],
      circumferenceSpreadThresholdCm: input.circumferenceSpreadThresholdCm as Centimetres,
    },
    now,
  )

  if (!written.ok) return err(written.error)
  return ok(written.value)
}

export function isPreferencesValidationError(
  error: PreferencesError,
): error is PreferencesValidationError {
  return error.kind === 'preferencesInvalid'
}

export function preferencesErrorMessage(error: PreferencesError): string {
  return isPreferencesValidationError(error)
    ? error.issues.map((entry) => entry.message).join(' ')
    : error.message
}

export { storageError }
